if (!customElements.get('flipzy-hygiene-gallery')) {
  customElements.define('flipzy-hygiene-gallery', class extends HTMLElement {
    connectedCallback() {
      const connection = this.connection = (this.connection || 0) + 1;
      Promise.all([
        customElements.whenDefined('slider-component'),
        customElements.whenDefined('media-gallery'),
      ]).then(() => {
        if (this.isConnected && connection === this.connection) this.initialize();
      });
    }

    initialize() {
      this.gallery = this.querySelector('media-gallery');
      this.track = this.querySelector('[data-hygiene-track]');
      this.dots = this.querySelector('[data-hygiene-dots]');
      this.viewer = this.track?.closest('slider-component');
      this.controls = this.querySelector('.flipzy-hygiene-gallery__controls');
      if (!this.track || !this.dots || !this.controls) return;

      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.track.addEventListener('scroll', () => this.scheduleSync(), { passive: true, signal });
      this.addEventListener('click', (event) => this.onClick(event), { capture: true, signal });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
      this.track.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal });
      this.track.addEventListener('pointercancel', (event) => this.onPointerEnd(event), { signal });
      this.track.addEventListener('lostpointercapture', () => this.finishDrag(), { signal });
      this.track.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        this.touch = { x: event.touches[0].clientX, y: event.touches[0].clientY, index: this.nearestIndex() };
      }, { passive: true, signal });
      this.track.addEventListener('touchend', (event) => {
        const touch = this.touch;
        this.touch = null;
        if (!touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - touch.x;
        const dy = event.changedTouches[0].clientY - touch.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.wrapGesture(touch.index, dx);
      }, { passive: true, signal });
      this.track.addEventListener('touchcancel', () => { this.touch = null; }, { passive: true, signal });
      this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

      this.observer = new MutationObserver(() => this.rebuild());
      this.observer.observe(this.track, { childList: true });
      this.resizeObserver = new ResizeObserver(() => {
        this.viewer.resetPages?.();
        this.scheduleSync();
      });
      this.resizeObserver.observe(this.track);
      this.rebuild();
      this.dataset.enhanced = '';
    }

    slideLabel(index) {
      // Whole translated sentence lets each language choose its own word order.
      return this.dataset.slideTemplate
        ? this.dataset.slideTemplate.replace('[index]', index + 1).replace('[total]', this.slides.length)
        : `${this.dataset.slideLabel} ${index + 1} ${this.dataset.ofLabel} ${this.slides.length}`;
    }

    rebuild() {
      this.slides = Array.from(this.track.children).filter((slide) => slide.matches('[data-media-id]'));
      this.dots.replaceChildren();
      this.slides.forEach((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'flipzy-hygiene-gallery__dot';
        button.dataset.hygieneSlide = String(index);
        button.setAttribute('aria-controls', this.track.id);
        button.setAttribute('aria-label', this.slideLabel(index));
        this.dots.append(button);
      });
      this.controls.hidden = this.slides.length < 2;
      this.resizeObserver.disconnect();
      this.resizeObserver.observe(this.track);
      this.slides.forEach((slide) => {
        const caption = slide.querySelector('.flipzy-hygiene-gallery__caption');
        if (caption) this.resizeObserver.observe(caption);
      });
      this.viewer.resetPages?.();
      this.sync();
    }

    nearestIndex() {
      const trackLeft = this.track.getBoundingClientRect().left;
      let nearest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, index) => {
        const currentDistance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (currentDistance < distance) {
          nearest = index;
          distance = currentDistance;
        }
      });
      return nearest;
    }

    scheduleSync() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    sync() {
      if (!this.slides?.length) return;
      const index = this.nearestIndex();
      this.currentIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        // Keep off-screen zoom and video controls out of the tab sequence.
        slide.inert = !active;
        const dot = this.dots.children[slideIndex];
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.controls.querySelector('[name="previous"]').disabled = this.slides.length < 2;
      this.controls.querySelector('[name="next"]').disabled = this.slides.length < 2;
    }

    goTo(index, focusDot = false) {
      const count = this.slides.length;
      if (!count) return;
      const nextIndex = ((index % count) + count) % count;
      const wrapped = index < 0 || index >= count;
      const slide = this.slides[nextIndex];
      if (!slide) return;
      const left = this.track.scrollLeft + slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollTo({ left, behavior: wrapped || this.motion.matches ? 'instant' : 'smooth' });
      if (focusDot) this.dots.children[nextIndex]?.focus({ preventScroll: true });
      this.announce(slide, nextIndex);
    }

    announce(slide, index) {
      const liveRegion = this.gallery.querySelector('[role="status"]');
      if (liveRegion) {
        liveRegion.removeAttribute('aria-hidden');
        liveRegion.textContent = this.slideLabel(index);
      }
      // Retain Dawn's deferred media playback when navigating to video/model slides.
      this.gallery.playActiveMedia?.(slide);
    }

    onClick(event) {
      if (this.suppressClickUntil > Date.now() && this.viewer.contains(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const dot = event.target.closest('[data-hygiene-slide]');
      if (dot && this.dots.contains(dot)) {
        event.preventDefault();
        this.goTo(Number(dot.dataset.hygieneSlide));
        return;
      }
      const arrow = event.target.closest('button[name="previous"], button[name="next"]');
      if (arrow && this.controls.contains(arrow)) {
        event.preventDefault();
        event.stopPropagation();
        this.goTo(this.nearestIndex() + (arrow.name === 'next' ? 1 : -1));
      }
    }

    onKeydown(event) {
      const onDots = this.dots.contains(event.target);
      if (!onDots && event.target !== this.track) return;
      const focusedIndex = onDots ? Number(event.target.dataset.hygieneSlide) : this.nearestIndex();
      const offsets = { ArrowLeft: -1, ArrowRight: 1 };
      let nextIndex;
      if (Object.hasOwn(offsets, event.key)) nextIndex = focusedIndex + offsets[event.key];
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = this.slides.length - 1;
      else return;
      event.preventDefault();
      this.goTo(nextIndex, onDots);
    }

    onPointerDown(event) {
      // Touch and trackpad swipes stay native; add familiar mouse dragging on photos.
      if (event.pointerType !== 'mouse' || event.button !== 0 || !event.target.closest('.product__modal-opener--image')) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.track.scrollLeft, index: this.nearestIndex(), movement: 0, active: false };
    }

    onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      if (event.buttons !== 1) {
        this.finishDrag();
        return;
      }
      const movement = event.clientX - this.drag.x;
      if (!this.drag.active) {
        if (Math.abs(movement) < 8 || Math.abs(movement) < Math.abs(event.clientY - this.drag.y)) return;
        this.drag.active = true;
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
      }
      this.drag.movement = movement;
      event.preventDefault();
      this.track.scrollLeft = this.drag.left - movement;
    }

    onPointerEnd(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      this.finishDrag();
    }

    finishDrag() {
      if (!this.drag) return;
      const { active, id, index, movement } = this.drag;
      this.drag = null;
      if (active) {
        this.suppressClickUntil = Date.now() + 250;
        this.track.classList.remove('is-dragging');
        if (!this.wrapGesture(index, movement)) this.goTo(this.nearestIndex());
      }
      if (this.track.hasPointerCapture(id)) this.track.releasePointerCapture(id);
    }

    wrapGesture(index, movement) {
      if (this.slides.length < 2 || Math.abs(movement) < 50) return false;
      if (index === 0 && movement > 0) this.goTo(-1);
      else if (index === this.slides.length - 1 && movement < 0) this.goTo(this.slides.length);
      else return false;
      this.suppressClickUntil = Date.now() + 250;
      return true;
    }

    disconnectedCallback() {
      this.connection = (this.connection || 0) + 1;
      this.events?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.drag = null;
      this.track?.classList.remove('is-dragging');
      this.slides?.forEach((slide) => { slide.inert = false; });
      delete this.dataset.enhanced;
    }
  });
}
